/**
 * Calendar Utilities for Add-To-Calendar Universal Links and .ics hand-off.
 * Converts course / event metadata into direct web URLs or triggers native system calendar.
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Parses date string (YYYY-MM-DD) and time string (e.g., "09:00 AM", "17:00")
 * Returns Date object in local time.
 */
export function parseDateTime(dateStr, timeStr, defaultHour = 9, defaultMinute = 0) {
  if (!dateStr) return new Date();
  
  const cleanDate = String(dateStr).split('T')[0];
  const parts = cleanDate.split('-').map(Number);
  const year = parts[0] || new Date().getFullYear();
  const month = (parts[1] || 1) - 1;
  const day = parts[2] || 1;

  let hour = defaultHour;
  let minute = defaultMinute;

  if (timeStr && typeof timeStr === 'string') {
    const trimmed = timeStr.trim();
    const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
    if (match) {
      let h = parseInt(match[1], 10);
      let m = parseInt(match[2], 10);
      const meridiem = match[3] ? match[3].toUpperCase() : null;
      if (meridiem === 'PM' && h < 12) h += 12;
      if (meridiem === 'AM' && h === 12) h = 0;
      hour = h;
      minute = m;
    }
  }

  return new Date(year, month, day, hour, minute, 0);
}

/**
 * Formats a Date into compact UTC format for Google Calendar: YYYYMMDDTHHmmssZ
 */
export function formatGoogleUtc(date) {
  const d = new Date(date);
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/**
 * Builds standard metadata object from course/event record
 */
export function normalizeEventData(course, user) {
  const trainerName = user?.fullName || user?.name || course?.TrainerName || 'Assigned Instructor';
  const title = `${course.CertificationBody ? course.CertificationBody + ' ' : ''}${course.CourseName} - ${course.Type || 'Class'}`;
  
  const portalUrl = window.location.origin;
  const description = [
    `Course: ${course.CertificationBody || ''} ${course.CourseName}`,
    `Type: ${course.Type || 'Class'}`,
    `Trainer: ${trainerName}`,
    `Venue: ${course.Venue || 'CCoE, Cyberjaya'}`,
    course.Description ? `Overview: ${course.Description}` : '',
    `\nPortal Link: ${portalUrl}/trainer/dashboard`
  ].filter(Boolean).join('\n');

  const location = course.Venue || (course.CourseName?.toLowerCase().includes('online') ? 'Online' : 'CCoE, Cyberjaya');
  
  const startDate = parseDateTime(course.StartDate, course.StartTime, 9, 0);
  const endDate = parseDateTime(course.EndDate || course.StartDate, course.EndTime, 17, 0);

  return {
    id: course.ScheduleID || course.id || 'event',
    title,
    description,
    location,
    startDate,
    endDate,
    rawCourse: course
  };
}

/**
 * Universal Link: Google Calendar
 * Opens calendar.google.com with prefilled event parameters.
 */
export function generateGoogleCalendarUrl(event) {
  const dates = `${formatGoogleUtc(event.startDate)}/${formatGoogleUtc(event.endDate)}`;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: dates,
    details: event.description,
    location: event.location
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Universal Link: Outlook Web (Live / Hotmail)
 */
export function generateOutlookLiveUrl(event) {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    startdt: event.startDate.toISOString(),
    enddt: event.endDate.toISOString(),
    subject: event.title,
    body: event.description,
    location: event.location
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/**
 * Universal Link: Office 365 Outlook Web
 */
export function generateOffice365Url(event) {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    startdt: event.startDate.toISOString(),
    enddt: event.endDate.toISOString(),
    subject: event.title,
    body: event.description,
    location: event.location
  });
  return `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/**
 * Backend Endpoint URL for direct RFC 5545 .ics stream
 */
export function getBackendIcsUrl(eventId) {
  return `${API_BASE}/api/calendar/event/${eventId}`;
}

/**
 * Native Calendar (.ics) Trigger Handler
 * 1. Checks if backend is responsive and downloads the official .ics with proper headers
 * 2. Falls back seamlessly to client-side Blob generation if offline or standalone
 * 3. Handles iOS Safari, Android Chrome, and desktop browsers without broken full-page reloads
 */
export async function triggerNativeIcsDownload(event, onToast) {
  const eventId = event.id;
  const backendUrl = getBackendIcsUrl(eventId);

  try {
    // Check if backend endpoint is accessible
    const response = await fetch(backendUrl, { method: 'GET' });
    if (response.ok) {
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `event-${eventId}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);

      if (onToast) {
        onToast({ type: 'success', msg: `Native calendar invite (.ics) ready for ${event.title}` });
      }
      return;
    }
  } catch (networkErr) {
    console.warn('[Calendar] Backend endpoint unavailable, using local RFC 5545 fallback', networkErr);
  }

  // Fallback: Generate valid RFC 5545 iCalendar client-side with alarms
  const startClean = formatGoogleUtc(event.startDate);
  const endClean = formatGoogleUtc(event.endDate);

  const fallbackIcs = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Cloudonuts CCoE//LMS Calendar Service//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:cloudonuts-${eventId}-${Date.now()}@ccoe.edu.my`,
    `SUMMARY:${event.title.replace(/\n/g, ' ')}`,
    `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}`,
    `LOCATION:${event.location}`,
    `DTSTART:${startClean}`,
    `DTEND:${endClean}`,
    'STATUS:CONFIRMED',
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    `DESCRIPTION:Reminder: ${event.title}`,
    'TRIGGER:-PT10M',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  const blob = new Blob([fallbackIcs], { type: 'text/calendar;charset=utf-8;' });
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = `event-${eventId}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(blobUrl);

  if (onToast) {
    onToast({ type: 'success', msg: `Calendar invite (.ics) downloaded!` });
  }
}
